import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, Badge, Spinner } from "../components";
import { QueryError } from "../components/QueryError";
import { apiGet, apiPost } from "../lib/api";
import { useToast } from "../contexts/ToastContext";

interface Backup {
  id: string;
  type: string;
  status: string;
  archivePath: string | null;
  archiveSizeBytes: number | null;
  includeVolumes: boolean;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
}

interface BackupSchedule {
  schedule: string;
  retain: number;
  enabled: boolean;
  manualBackupAvailable?: boolean;
  manualBackupReason?: string | null;
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatDuration(start: string, end: string | null): string {
  if (!end) return "running...";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(0)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
}

function statusColor(status: string): "green" | "red" | "blue" | "neutral" {
  switch (status) {
    case "completed":
      return "green";
    case "failed":
      return "red";
    case "running":
      return "blue";
    default:
      return "neutral";
  }
}

export default function Backups() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [includeVolumes, setIncludeVolumes] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["backups"],
    queryFn: () => apiGet<{ backups: Backup[]; total: number }>("/api/backups?limit=50"),
    refetchInterval: 10_000,
  });

  const { data: schedule } = useQuery({
    queryKey: ["backups-schedule"],
    queryFn: () => apiGet<BackupSchedule>("/api/backups/schedule"),
  });

  const createBackup = useMutation({
    mutationFn: () => apiPost<{ id: string }>("/api/backups", { includeVolumes }),
    onSuccess: () => {
      toast.success("Backup triggered");
      queryClient.invalidateQueries({ queryKey: ["backups"] });
    },
    onError: (err: Error) => toast.error("Failed to trigger backup", err.message),
  });

  const backups = data?.backups ?? [];
  const hasRunning = backups.some((backup) => backup.status === "running");
  const manualBackupAvailable = schedule?.manualBackupAvailable !== false;
  const manualBackupReason =
    schedule?.manualBackupReason ?? "Manual backup is not available on this environment.";

  if (error && !data) {
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Backups</h1>
            <p style={{ margin: "4px 0 0", color: "var(--c-text-secondary)", fontSize: 14 }}>
              Platform backup management and scheduling
            </p>
          </div>
        </div>
        <QueryError error={error} queryKeys={[["backups"]]} />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Backups</h1>
          <p style={{ margin: "4px 0 0", color: "var(--c-text-secondary)", fontSize: 14 }}>
            Platform backup management and scheduling
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "var(--c-text-secondary)",
            }}
          >
            <input
              type="checkbox"
              checked={includeVolumes}
              onChange={(e) => setIncludeVolumes(e.target.checked)}
            />
            Include volumes
          </label>
          <button
            onClick={() => createBackup.mutate()}
            disabled={createBackup.isPending || hasRunning || !manualBackupAvailable}
            style={{
              padding: "8px 16px",
              backgroundColor: "var(--c-primary)",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 500,
              cursor:
                createBackup.isPending || hasRunning || !manualBackupAvailable
                  ? "not-allowed"
                  : "pointer",
              opacity: createBackup.isPending || hasRunning || !manualBackupAvailable ? 0.6 : 1,
            }}
          >
            {!manualBackupAvailable
              ? "Manual Backup Unavailable"
              : createBackup.isPending
                ? "Starting..."
                : "Create Backup"}
          </button>
        </div>
      </div>

      {schedule && (
        <Card style={{ marginBottom: 20, padding: 16 }}>
          <div style={{ display: "flex", gap: 32, fontSize: 13, alignItems: "center" }}>
            <div>
              <span style={{ color: "var(--c-text-secondary)" }}>Schedule: </span>
              <span style={{ fontWeight: 500 }}>
                {schedule.enabled ? `Cron: ${schedule.schedule}` : "Disabled"}
              </span>
            </div>
            <div>
              <span style={{ color: "var(--c-text-secondary)" }}>Retention: </span>
              <span style={{ fontWeight: 500 }}>{schedule.retain} backups</span>
            </div>
            <div>
              <span style={{ color: "var(--c-text-secondary)" }}>Total backups: </span>
              <span style={{ fontWeight: 500 }}>{data?.total ?? 0}</span>
            </div>
            <span
              style={{
                marginLeft: "auto",
                fontSize: 12,
                color: "var(--c-text-secondary)",
              }}
            >
              {manualBackupAvailable
                ? "Schedule is read-only in Adam until persistent backup policy management exists."
                : manualBackupReason}
            </span>
          </div>
        </Card>
      )}

      <Card>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <Spinner size="md" />
          </div>
        ) : backups.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--c-text-secondary)" }}>
            No backups yet. Click "Create Backup" to create your first backup.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.1))" }}>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Date
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Type
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Status
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Size
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Duration
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Volumes
                </th>
              </tr>
            </thead>
            <tbody>
              {backups.map((backup) => (
                <tr
                  key={backup.id}
                  style={{ borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.05))" }}
                >
                  <td style={{ padding: "10px 12px" }}>
                    {new Date(backup.startedAt).toLocaleString()}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <Badge
                      variant={
                        backup.type === "manual"
                          ? "blue"
                          : backup.type === "pre_migration"
                            ? "purple"
                            : "neutral"
                      }
                    >
                      {backup.type}
                    </Badge>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <Badge variant={statusColor(backup.status)}>{backup.status}</Badge>
                    {backup.error && (
                      <span
                        style={{ marginLeft: 8, color: "var(--c-danger)", fontSize: 12 }}
                        title={backup.error}
                      >
                        {backup.error.length > 40
                          ? `${backup.error.slice(0, 40)}...`
                          : backup.error}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px" }}>{formatBytes(backup.archiveSizeBytes)}</td>
                  <td style={{ padding: "10px 12px" }}>
                    {formatDuration(backup.startedAt, backup.completedAt)}
                  </td>
                  <td style={{ padding: "10px 12px" }}>{backup.includeVolumes ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
